import { Autocomplete, Box } from "@mui/material";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { FieldArray, FormikProvider, useFormik } from "formik";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Accordion, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchCurrencyByDropDown } from "slices/Masters/currency/thunk";
import { fetchWarehouseDropDown } from "slices/Masters/warehosue/thunk";
import {
  fetchApprovePurchaseRequestDropdownBySupplierId,
  fetchApprovePurchaseRequestDropdownBySupplierIdForEdit,
  fetchApprovePurchaseRequestId,
  fetchSupplierProductsByPRIDS,
} from "slices/Procurement/purchase-order/approved-pr/thunk";
import { fetchOrganizationDropdown } from "slices/UserManagement/organisation/thunk";
import * as Yup from "yup";
import SupplierDetails from "../CreateDirectPO/Components/SupplierDetails";
import ProductFormTable from "../CreateDirectPO/Components/ProductFormTable";
import { fetchBranchDropdownBySupplierID } from "slices/Masters/supplier/thunk";
import { fetchSupplierDropDown } from "slices/Masters/supplier/thunk";
import { showConfirm, showCustomSwal } from "helpers/swalHelper";
import {
  addPurchaseOrder,
  fetchPurchaseOrderId,
  updatePurchaseOrder,
} from "slices/Procurement/purchase-order/purchase-order/thunk";
import { fetchPurchaseRequestId } from "slices/Procurement/purchase-request/thunk";
import AddTagsForPurchaseOrder from "../../Components/AddTagsForPurchaseOrder";
import LoadingSpinner from "components/LoadingSpinner";
import { ChevronDown, ChevronUp, Plus, Trash } from "lucide-react";
import { fetchTermsAndConditionByDropDown } from "slices/Masters/terms-and-condition/thunk";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import usePermission from "hooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import { formatIndianAmount } from "helpers/formatIndianAmount";

interface FormValues {
  with_pr: boolean;
  prs: any;
  po_date: string;
  eta_date: string;
  supplier_id: string;
  contact_person: any;
  supplier_branch_id: string;
  ship_to: any;
  bill_to: any;
  initiated_by: string;
  currency: any;
  exchange_rate: string;
  notes: string;
  terms_and_conditions: any[];
  attachments: (File | string)[];
  products: any[];
  tags: string[];
}

const EditPurchaseOrder: React.FC = () => {
  const { po_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { state } = useLocation();
  const isEdit = Boolean(po_id);
  const [pr_supplier_data, set_pr_supplier_data] = useState<any>(null);
  const [shipToOptions, setShipToOptions] = useState<any[]>([]);
  const [billToOptions, setBillToOptions] = useState<any[]>([]);
  const [purchaseRequestOptions, setPurchaseRequestOptions] = useState<any[]>(
    [],
  );
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [supplierLoading, setSupplierLoading] = useState(true);

  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<any[]>([]);

  const productsRef = React.useRef<any[]>([]);
  const initialEditLoadDoneRef = React.useRef(false);
  const hasSkippedInitialPRFetchRef = React.useRef(false);
  const prevPrsRef = React.useRef<any[]>([]);
  const initialPrOptionsRef = React.useRef<any[]>([]);
  const initialSupplierIdRef = React.useRef<string>("");
  const initialTagsRef = React.useRef<string[]>([]);
  const customTagsRef = React.useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<
    File | File[] | null | string | string[] | (File | string)[]
  >(null);
  const [initialFileExists, setInitialFileExists] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [existingDocuments, setExistingDocuments] = useState<any>([]);
  const [removedProducts, setRemovedProducts] = useState<any[]>([]);

  // Terms and Conditions (CreateDirectPO-style UI)
  const { termsAndConditionsList, loading: termsLoading } = useSelector(
    (state: any) => state.TermsAndCondition,
  );
  const [newTerm, setNewTerm] = useState({ type: "", options: [] as string[] });
  const [termTypeOptions, setTermTypeOptions] = useState<string[]>([]);
  const [termOptionMap, setTermOptionMap] = useState<Record<string, string[]>>(
    {},
  );

  const { permissions } = usePermission(
    app_modules.purchase_management.app_name,
    app_modules.purchase_management.modules.purchase_order,
  );
  const canCreateDirectPO = permissions?.DirectPO ?? true;

  useEffect(() => {
    dispatch(fetchTermsAndConditionByDropDown() as any);
  }, [dispatch]);

  useEffect(() => {
    const list = termsAndConditionsList as { type: string; options: string }[];
    if (list && list.length > 0) {
      const uniqueTypes = Array.from(
        new Set(list.map((item) => String(item.type || ""))),
      ) as string[];
      setTermTypeOptions(uniqueTypes);
      const optionsMap: Record<string, string[]> = {};
      uniqueTypes.forEach((type) => {
        optionsMap[type] = list
          .filter((item) => item.type === type)
          .map((item) => String(item.options || ""));
      });
      setTermOptionMap(optionsMap);
    }
  }, [termsAndConditionsList]);

  const handleAddTerm = () => {
    if (!newTerm.type || newTerm.options.length === 0) {
      return;
    }

    // Ensure we're working with an array
    const currentTerms = Array.isArray(formik.values.terms_and_conditions)
      ? formik.values.terms_and_conditions
      : [];

    // Check if a term with this type already exists
    const existingTermIndex = currentTerms.findIndex(
      (t: any) => t.type === newTerm.type,
    );

    if (existingTermIndex >= 0) {
      const updatedTerms = [...currentTerms];
      // Replace existing options with the currently selected options
      const combinedOptions = newTerm.options;

      const existingTerm = currentTerms[existingTermIndex];

      updatedTerms[existingTermIndex] = {
        ...existingTerm,
        description: combinedOptions.join("\n"),
        option: combinedOptions[0] || "",
        options: combinedOptions.join("\n"), // Update this too for consistency
      };
      formik.setFieldValue("terms_and_conditions", updatedTerms);
    } else {
      const term = {
        id: Date.now().toString(),
        type: newTerm.type,
        option: newTerm.options[0] || "",
        description: newTerm.options.join("\n"),
        options: newTerm.options.join("\n"),
      };

      formik.setFieldValue("terms_and_conditions", [...currentTerms, term]);
    }

    setNewTerm({ type: "", options: [] });
  };

  const getTermOptions = (term: any): string[] => {
    const text = term.description ?? term.options ?? term.option;
    if (text && typeof text === "string" && text.includes("\n")) {
      return text.split("\n").filter(Boolean);
    }
    return text ? [text] : [];
  };

  const getAvailableOptions = () => {
    if (!newTerm.type) return [];
    return termOptionMap[newTerm.type] || [];
  };

  // Helper function to recalculate product values with exchange rate
  const recalculateProductsWithExchangeRate = (
    productsArray: any[],
    exchangeRate: number,
  ) => {
    if (!exchangeRate || exchangeRate <= 0) {
      // If exchange rate is invalid, reset to initial state (using unit_price directly)
      return productsArray.map((prod: any) => {
        const originalUnitPrice = Number(prod?.unit_price) || 0;
        const orderQuantity = Number(prod?.order_quantity) || 0;
        const gstPercent = Number(prod?.gst_percent) || 0;

        // Calculate without exchange rate (using unit_price directly)
        const itemAmount = orderQuantity * originalUnitPrice;
        const discountAmount = Number(prod?.discount_amount) || 0;
        const netTaxableValue = itemAmount - discountAmount;
        const gstAmount = (netTaxableValue * gstPercent) / 100;
        const total = netTaxableValue + gstAmount;

        const roundToTwo = (num: number) => Math.round(num * 100) / 100;

        return {
          ...prod,
          rate_in_currency: 0,
          item_amount: roundToTwo(itemAmount),
          net_taxable_value: roundToTwo(netTaxableValue),
          total: roundToTwo(total),
          gst_amount: roundToTwo(gstAmount),
        };
      });
    }

    return productsArray.map((prod: any) => {
      // Get original unit_price (preserve it)
      const originalUnitPrice = Number(prod?.unit_price) || 0;
      const orderQuantity = Number(prod?.order_quantity) || 0;
      const gstPercent = Number(prod?.gst_percent) || 0;

      // Calculate rate in currency: unit_price / exchange_rate
      const rateInCurrency = originalUnitPrice / exchangeRate;

      // Calculate gross value (item amount) = order_quantity * rate_in_currency
      const itemAmount = orderQuantity * rateInCurrency;

      // Calculate net taxable value (gross value - discount if any)
      const discountAmount = Number(prod?.discount_amount) || 0;
      const netTaxableValue = itemAmount - discountAmount;

      // Calculate GST amount
      const gstAmount = (netTaxableValue * gstPercent) / 100;

      // Calculate total = net_taxable_value + gst_amount
      const total = netTaxableValue + gstAmount;

      // Round to 2 decimal places
      const roundToTwo = (num: number) => Math.round(num * 100) / 100;

      return {
        ...prod,
        rate_in_currency: roundToTwo(rateInCurrency),
        item_amount: roundToTwo(itemAmount),
        net_taxable_value: roundToTwo(netTaxableValue),
        total: roundToTwo(total),
        gst_amount: roundToTwo(gstAmount),
      };
    });
  };

  // Helper function to calculate totals from products
  const calculateTotals = (productsArray: any[]) => {
    if (!productsArray || productsArray.length === 0) {
      return {
        subtotal: 0,
        totalGst: 0,
        grandTotal: 0,
      };
    }

    const subtotal = productsArray.reduce((sum, prod) => {
      const netTaxableValue = Number(prod?.net_taxable_value) || 0;
      return sum + netTaxableValue;
    }, 0);

    const totalGst = productsArray.reduce((sum, prod) => {
      const netTaxableValue = Number(prod?.net_taxable_value) || 0;
      const total = Number(prod?.total) || 0;
      // GST amount = total - net_taxable_value
      return sum + (total - netTaxableValue);
    }, 0);

    const grandTotal = productsArray.reduce((sum, prod) => {
      const total = Number(prod?.total) || 0;
      return sum + total;
    }, 0);

    return {
      subtotal,
      totalGst,
      grandTotal,
    };
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      with_pr: true,
      prs: [],
      po_date: new Date().toISOString()?.split("T")?.[0],
      eta_date: "",
      supplier_id: state ? state?.supplier_id : "",
      contact_person: null,
      supplier_branch_id: "",
      ship_to: null,
      bill_to: null,
      initiated_by: "",
      currency: null,
      exchange_rate: "",
      notes: "",
      terms_and_conditions: [],
      attachments: [],
      products: [],
      tags: [],
    },
    validationSchema: Yup.object({
      supplier_id: Yup.string().required("Please select Supplier"),
      contact_person: Yup.object().required("Please select Contact Person"),
      supplier_branch_id: Yup.string().required(
        "Please select Supplier Branch",
      ),
      prs: Yup.array().when("with_pr", {
        is: true,
        then: (schema: any) =>
          schema
            .min(1, "Please select at least one PR No.")
            .required("Please select at least one PR No."),
        otherwise: (schema: any) => schema.notRequired(),
      }),
      po_date: Yup.string().required("Please select PO Date"),
      initiated_by: Yup.string().required("Intiated by is required"),
      eta_date: Yup.string().optional(),
      ship_to: Yup.object().nullable().required("Please select Ship To"),
      bill_to: Yup.object().nullable().required("Please select Bill To"),
      currency: Yup.object().nullable().required("Currency is required"),
      exchange_rate: Yup.string()
        .required("Exchange Rate is required")
        .test(
          "is-valid-number",
          "Exchange Rate must be a valid positive number",
          (value) => {
            if (!value) return false;
            const regex = /^\d*\.?\d*$/;
            if (!regex.test(value)) return false;
            const numValue = parseFloat(value);
            return !isNaN(numValue) && numValue > 0;
          },
        ),
      products: Yup.array()
        .min(1, "Add at least one PO item before submitting.")
        .required("Add at least one PO item before submitting."),
    }),
    onSubmit: async (values) => {
      if (!values.products || values.products.length === 0) {
        toast.error("Add at least one PO item before submitting.");
        return;
      }
      setLoading(true);
      const calculatedTotals = calculateTotals(values.products);

      // Check if there are new files to upload (File objects)
      // const hasNewFiles =
      //   attachedFiles &&
      //   ((Array.isArray(attachedFiles) &&
      //     attachedFiles.length > 0 &&
      //     attachedFiles.some((f: any) => f instanceof File)) ||
      //     attachedFiles instanceof File);
      const attachments = formik.values.attachments;

      const hasFiles =
        Array.isArray(attachments) &&
        attachments.some((item): item is File => typeof item !== "string");

      const hasExistingDocuments =
        Array.isArray(existingDocuments) &&
        existingDocuments.some(
          (item): item is string => typeof item === "string",
        );

      // Check if files were removed (attachedFiles is null or empty AND there was an initial file)

      // Use FormData if:
      // 1. New files are uploaded (File objects)
      // 2. Files were removed (had file initially, now removed)
      // Don't use FormData if only existing files (string URLs) remain unchanged
      if (hasFiles || hasExistingDocuments) {
        const formData = new FormData();

        // Supplier details
        if (pr_supplier_data?.supplier_name)
          formData.append("supplier_name", pr_supplier_data.supplier_name);
        if (pr_supplier_data?.supplier_type)
          formData.append("supplier_type", pr_supplier_data.supplier_type);
        if (pr_supplier_data?.supplier_id)
          formData.append("supplier_id", pr_supplier_data.supplier_id);
        if (pr_supplier_data?.supplier_details)
          formData.append(
            "supplier_details",
            JSON.stringify(pr_supplier_data.supplier_details),
          );
        if (pr_supplier_data?.email)
          formData.append("email", pr_supplier_data.email);
        if (pr_supplier_data?.phone_number)
          formData.append("phone_number", pr_supplier_data.phone_number);
        if (pr_supplier_data?.contact_person_name)
          formData.append(
            "contact_person_name",
            pr_supplier_data.contact_person_name,
          );
        if (pr_supplier_data?.contact_person_email)
          formData.append(
            "contact_person_email",
            pr_supplier_data.contact_person_email,
          );
        if (pr_supplier_data?.contact_person_phone_number)
          formData.append(
            "contact_person_phone_number",
            pr_supplier_data.contact_person_phone_number,
          );
        if (values?.supplier_branch_id)
          formData.append("supplier_branch_id", values.supplier_branch_id);
        formData.append("supplier_branch_details", JSON.stringify({}));

        // PR IDs
        if (values?.prs && Array.isArray(values.prs)) {
          formData.append("pr_ids", JSON.stringify(values.prs));
        }
        formData.append("tags", JSON.stringify(values?.tags ?? []));

        // PO details
        formData.append("po_date", new Date(values?.po_date).toISOString());
        if (values?.initiated_by)
          formData.append("initiated_by", values.initiated_by);
        if (values?.eta_date)
          formData.append("eta_date", new Date(values.eta_date).toISOString());
        if (values?.bill_to?.organization_id)
          formData.append("bill_to_id", values.bill_to.organization_id);
        if (values?.ship_to?.warehouse_id)
          formData.append("ship_to_id", values.ship_to.warehouse_id);
        if (values?.currency?.currency_id)
          formData.append("currency_id", values.currency.currency_id);
        formData.append("exchange_rate", String(values?.exchange_rate));
        if (values?.notes) formData.append("notes", values.notes);
        if (values?.terms_and_conditions?.length > 0) {
          const normalizedTerms = values.terms_and_conditions.map((t: any) => ({
            type: t.type || "general",
            options: t.description ?? t.options ?? t.option ?? "",
          }));
          formData.append(
            "terms_and_conditions",
            JSON.stringify(normalizedTerms),
          );
        }

        // Totals
        formData.append("subtotal", String(calculatedTotals.subtotal));
        formData.append("total_gst_value", String(calculatedTotals.totalGst));
        formData.append("total", String(calculatedTotals.grandTotal));

        // Products
        formData.append(
          "products",
          JSON.stringify(
            values.products?.map((prod: any) => ({
              product_id: prod?.product_id || prod?.product_details?.product_id,
              product_name: prod?.product_name,
              product_code: prod?.product_code,
              product_details: prod?.product_details,
              order_quantity: prod?.order_quantity,
              balance_quantity: prod?.order_quantity,
              damaged_quantity: 0,
              quantity: 0,
              unit_name: prod?.unit_name,
              unit_id: prod?.unit_id,
              unit_price: Number(prod?.unit_price),
              rate_in_currency: prod?.rate_in_currency,
              item_amount: Number(prod?.item_amount),
              net_taxable_value: Number(prod?.net_taxable_value),
              gst_percent: Number(prod?.gst_percent),
              igst_percent: Number(prod?.igst_percent),
              cgst_percent: Number(prod?.cgst_percent),
              sgst_percent: Number(prod?.sgst_percent),
              total: Number(prod?.total),
              delivery_lead_time: prod?.delivery_lead_time,
              eta_date: prod?.eta_date || "",
              description: prod?.description,
              remark: prod?.remark,
              pr_product_id: prod?.pr_product_id || null,
              pr_id: prod?.pr_id || null,
            })),
          ),
        );

        // Handle file upload or removal
        // if (filesRemoved) {
        //   // File was removed - send null to remove it
        //   formData.append("po_document", "");
        //   formData.append("remove_po_document", "true");
        // } else if (hasNewFiles) {
        //   // New file uploaded - backend expects single file with field name 'po_document'
        //   if (Array.isArray(attachedFiles)) {
        //     // If multiple files, take the first File object (not string URL)
        //     const fileToUpload = attachedFiles.find(
        //       (f: any) => f instanceof File,
        //     );
        //     if (fileToUpload) {
        //       formData.append("po_document", fileToUpload);
        //     }
        //   } else if (attachedFiles instanceof File) {
        //     formData.append("po_document", attachedFiles);
        //   }
        // }

        if (values.attachments?.length) {
          values.attachments.forEach((item: any) => {
            const file =
              item instanceof File ? item : item.file || item.originFileObj;

            if (file instanceof File) {
              formData.append("attachments", file); // ✅ NOT attachments[]
            }
          });
        }

        if (existingDocuments.length > 0) {
          formData.append(
            "existingDocuments",
            JSON.stringify(existingDocuments ?? []),
          );
        }

        try {
          if (po_id) {
            await dispatch(updatePurchaseOrder({ formData, po_id })).unwrap();
            toast.success("Purchase Order Updated Successfully");
            navigate("/purchase-management/purchase-order");
          }
        } catch (e) {
          handleSubmissionError(e);
        } finally {
          setLoading(false);
        }
      } else {
        // No file changes, send as JSON
        const payload = {
          // Supplier details
          supplier_name: pr_supplier_data?.supplier_name,
          supplier_type: pr_supplier_data?.supplier_type,
          supplier_id: pr_supplier_data?.supplier_id,
          supplier_details: pr_supplier_data?.supplier_details,
          email: pr_supplier_data.email || null,
          phone_number: pr_supplier_data.phone_number || null,
          contact_person_name: pr_supplier_data.contact_person_name || null,
          contact_person_email: pr_supplier_data.contact_person_email || null,
          contact_person_phone_number:
            pr_supplier_data.contact_person_phone_number || null,
          supplier_branch_id: values?.supplier_branch_id,
          supplier_branch_details: {}, //oprional need to remove
          // ====================
          pr_ids: values?.prs, //for pr reference
          tags: values?.tags ?? [],
          //
          po_date: new Date(values?.po_date),
          initiated_by: values?.initiated_by,
          eta_date: values?.eta_date ? new Date(values.eta_date) : undefined,
          bill_to_id: values?.bill_to?.organization_id,
          ship_to_id: values?.ship_to?.warehouse_id,
          currency_id: values?.currency?.currency_id,
          exchange_rate: Number(values?.exchange_rate),
          notes: values?.notes,
          terms_and_conditions: (values?.terms_and_conditions || []).map(
            (t: any) => ({
              type: t.type || "general",
              options: t.description ?? t.options ?? t.option ?? "",
            }),
          ),
          subtotal: calculatedTotals.subtotal,
          total_gst_value: calculatedTotals.totalGst,
          total: calculatedTotals.grandTotal,
          products: values.products?.map((prod: any) => ({
            product_id: prod?.product_id || prod?.product_details?.product_id,
            product_name: prod?.product_name,
            product_code: prod?.product_code,
            product_details: prod?.product_details,
            order_quantity: prod?.order_quantity,
            balance_quantity: prod?.order_quantity,
            damaged_quantity: 0,
            quantity: 0,
            unit_name: prod?.unit_name,
            unit_id: prod?.unit_id,
            unit_price: Number(prod?.unit_price),
            rate_in_currency: prod?.rate_in_currency,
            item_amount: Number(prod?.item_amount),
            net_taxable_value: Number(prod?.net_taxable_value),
            gst_percent: Number(prod?.gst_percent),
            igst_percent: Number(prod?.igst_percent),
            cgst_percent: Number(prod?.cgst_percent),
            sgst_percent: Number(prod?.sgst_percent),
            total: Number(prod?.total),
            delivery_lead_time: prod?.delivery_lead_time,
            eta_date: prod?.eta_date || "",
            description: prod?.description,
            remark: prod?.remark,
            pr_product_id: prod?.pr_product_id || null,
            pr_id: prod?.pr_id || null,
          })),
        };

        try {
          if (po_id) {
            await dispatch(
              updatePurchaseOrder({ formData: payload, po_id }),
            ).unwrap();
            toast.success("Purchase Order Updated Successfully");
            navigate("/purchase-management/purchase-order");
          }
        } catch (e) {
          handleSubmissionError(e);
        } finally {
          setLoading(false);
        }
      }

      function handleSubmissionError(err: any) {
        if (typeof err === "string") {
          toast.error(err);
          return;
        }

        // If error is an object (from err.response?.data)
        if (err && typeof err === "object") {
          const backendMessage = err?.message;
          const errType = err?.errors || {};

          if (errType && Object.keys(errType).length > 0) {
            Object.keys(errType).forEach((key: string) => {
              // if errors are nested arrays/objects, show first message
              const msg = Array.isArray((errType as any)[key])
                ? (errType as any)[key][0]
                : JSON.stringify((errType as any)[key]);
              showCustomSwal({ icon: "error", text: `${msg}` });
            });
            return;
          }

          if (backendMessage) {
            toast.error(backendMessage);
            return;
          }
        }

        // Handle direct axios errors (fallback - should not happen with unwrap)
        const status = err?.response?.status;
        const axiosMessage = err?.response?.data?.message;
        const axiosErrType = err?.response?.data?.errors || {};

        if (axiosErrType && Object.keys(axiosErrType).length > 0) {
          Object.keys(axiosErrType).forEach((key: string) => {
            const msg = Array.isArray((axiosErrType as any)[key])
              ? (axiosErrType as any)[key][0]
              : JSON.stringify((axiosErrType as any)[key]);
            showCustomSwal({ icon: "error", text: `${msg}` });
          });
          return;
        }

        if (status === 404 && axiosMessage) {
          toast.error(axiosMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  useEffect(() => {
    setPageLoading(true);
    setSupplierLoading(true);
    Promise.all([
      dispatch(fetchOrganizationDropdown()).unwrap(),
      dispatch(fetchWarehouseDropDown()).unwrap(),
      dispatch(fetchCurrencyByDropDown()).unwrap(),
      dispatch(fetchSupplierDropDown()).unwrap(),
    ])
      .then(([bill_to_res, ship_to_res, currency_res, supplier_res]: any[]) => {
        setShipToOptions(ship_to_res?.data || []);
        setBillToOptions(bill_to_res || []);
        const currencies = currency_res?.data || [];
        setCurrencyOptions(currencies);
        setSupplierOptions(supplier_res?.data || []);
      })
      .catch((err: any) => {
        console.error("Failed to fetch Dropdown:", err);
        toast.error("failed to fetch dropdowns");
      })
      .finally(() => {
        setPageLoading(false);
        setSupplierLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!po_id) return;
    const fetchPrData = async () => {
      try {
        setPageLoading(true);
        const po_res = await dispatch(fetchPurchaseOrderId(po_id)).unwrap();

        const poData = po_res?.data;
        const hasPRs =
          Array.isArray(poData?.purchase_order_purchase_requests) &&
          poData.purchase_order_purchase_requests.length > 0;
        const supplierData = {
          supplier_id: poData?.supplier_id,
          supplier_name: poData?.supplier_name,
          supplier_type: poData?.supplier_type,
          email: poData?.email,
          phone_number: poData?.phone_number,
          contact_person_name: poData?.contact_person_name,
          contact_person_email: poData?.contact_person_email,
          contact_person_phone_number: poData?.contact_person_phone_number,
        };
        set_pr_supplier_data(supplierData);

        const [prDropdownRes, branchOptionsRes] = await Promise.all([
          dispatch(
            fetchApprovePurchaseRequestDropdownBySupplierIdForEdit({
              supplier_id: poData.supplier_id,
              po_id: po_id,
            }),
          ).unwrap(),
          dispatch(
            fetchBranchDropdownBySupplierID(poData.supplier_id),
          ).unwrap(),
        ]);

        setPurchaseRequestOptions(prDropdownRes?.data || []);

        // Merge initial PRs into options to ensure they appear in Edit mode
        const initialPrs = poData?.purchase_order_purchase_requests || [];
        const formattedInitialPrs = initialPrs.map((pr: any) => ({
          pr_id: pr.pr_id,
          pr_no: pr.purchase_request?.pr_no || pr.pr_no || "N/A",
        }));
        initialPrOptionsRef.current = formattedInitialPrs;
        initialSupplierIdRef.current = poData.supplier_id;

        setPurchaseRequestOptions((prev) => {
          const merged = [...prev];
          formattedInitialPrs.forEach((prObj: any) => {
            if (!merged.some((opt: any) => opt.pr_id === prObj.pr_id)) {
              merged.push(prObj);
            }
          });
          return merged;
        });
        setBranchOptions(branchOptionsRes?.data || []);
        formik.setFieldValue("po_date", poData?.po_date?.split("T")?.[0]);
        formik.setFieldValue("eta_date", poData?.eta_date?.split("T")?.[0]);
        formik.setFieldValue("initiated_by", poData?.initiated_by);
        formik.setFieldValue("supplier_branch_id", poData?.supplier_branch_id);
        formik.setFieldValue("bill_to", poData?.bill_to);
        formik.setFieldValue("ship_to", poData?.ship_to);
        formik.setFieldValue("currency", poData?.currency);
        formik.setFieldValue("exchange_rate", Number(poData?.exchange_rate));
        formik.setFieldValue("notes", poData?.notes);
        formik.setFieldValue("with_pr", hasPRs);
        formik.setFieldValue("supplier_id", poData?.supplier_id);
        formik.setFieldValue("contact_person", poData?.contact_person || null);
        formik.setFieldValue("products", poData?.products || []);
        const rawTerms = poData?.terms_and_conditions;
        const terms = Array.isArray(rawTerms)
          ? rawTerms
          : typeof rawTerms === "string"
            ? (() => {
                try {
                  const parsed = JSON.parse(rawTerms);
                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              })()
            : [];
        formik.setFieldValue("terms_and_conditions", terms);
        formik.setFieldValue(
          "prs",
          poData?.purchase_order_purchase_requests?.map((pr: any) => pr?.pr_id),
        );
        const storedTags = Array.isArray(poData?.tags) ? poData.tags : [];
        formik.setFieldValue("tags", storedTags);
        initialTagsRef.current = [...storedTags];

        // Load existing file if available
        // if (poData?.po_document) {
        //   setAttachedFiles(poData.po_document);
        //   setInitialFileExists(true);
        // }
        if (poData?.attachments) {
          setExistingDocuments(poData.attachments || []);
        }
        initialEditLoadDoneRef.current = true;
      } catch (err: any) {
        console.error("Failed to fetch PR data:", err);
        toast.error(err?.message || "Something went wrong");
      } finally {
        setPageLoading(false);
      }
    };

    fetchPrData();
  }, [po_id, dispatch]);

  useEffect(() => {
    const supplierId = formik.values.supplier_id;
    if (!supplierId) {
      set_pr_supplier_data(null);
      setBranchOptions([]);
      setPurchaseRequestOptions([]);
      return;
    }
    const selected = supplierOptions.find(
      (s: any) => s.supplier_id === supplierId,
    );
    if (selected) {
      set_pr_supplier_data((prev: any) => {
        const merged = { ...selected };
        if (po_id && prev?.contact_person_name) {
          merged.contact_person_name = prev.contact_person_name;
        }
        if (po_id && prev?.contact_person_email) {
          merged.contact_person_email = prev.contact_person_email;
        }
        if (po_id && prev?.contact_person_phone_number) {
          merged.contact_person_phone_number = prev.contact_person_phone_number;
        }
        return merged;
      });
    }
    const fetchData = async () => {
      try {
        const [prRes, branchRes] = await Promise.all([
          dispatch(
            fetchApprovePurchaseRequestDropdownBySupplierId(supplierId),
          ).unwrap(),
          dispatch(fetchBranchDropdownBySupplierID(supplierId)).unwrap(),
        ]);

        let finalPrOptions = prRes?.data || [];
        // If supplier matches original PO, merge the initial PRs back in
        if (supplierId === initialSupplierIdRef.current) {
          const merged = [...finalPrOptions];
          initialPrOptionsRef.current.forEach((prObj: any) => {
            if (!merged.some((opt: any) => opt.pr_id === prObj.pr_id)) {
              merged.push(prObj);
            }
          });
          finalPrOptions = merged;
        }

        setPurchaseRequestOptions(finalPrOptions);
        setBranchOptions(branchRes?.data || []);
      } catch (err) {
        console.error("Failed to fetch supplier data:", err);
      }
    };
    fetchData();
  }, [formik.values.supplier_id, supplierOptions, po_id, dispatch]);

  const handleWithPRToggle = async () => {
    const isWithPR = !formik.values.with_pr;
    const hasProducts = formik.values.products?.length > 0;

    if (hasProducts) {
      const result = await showConfirm(
        isWithPR
          ? "Direct products are already added. Switching to 'With PR' will remove them. Do you want to continue?"
          : "PR-based products are already added. Switching to Direct mode will remove them. Do you want to continue?",
        "Change Purchase Mode?",
        "Yes, Continue",
        "Cancel",
      );
      if (!result.isConfirmed) return;
      formik.setFieldValue("products", []);
      if (!isWithPR) formik.setFieldValue("prs", []);
    }

    formik.setFieldValue("with_pr", isWithPR);
  };

  // When PRs change in edit: tags = remaining PR tags + stored PO tags + custom tags added in session; deselecting a PR removes only that PR's tags
  useEffect(() => {
    if (!formik.values.with_pr || !initialEditLoadDoneRef.current) return;
    const prIds = formik.values.prs;
    const storedTags = initialTagsRef.current ?? [];
    const customTags = Array.from(customTagsRef.current);
    const baseTags = Array.from(new Set([...storedTags, ...customTags]));
    if (!Array.isArray(prIds) || prIds.length === 0) {
      formik.setFieldValue("tags", baseTags);
      return;
    }
    Promise.all(
      prIds.map((id: string) =>
        dispatch(fetchPurchaseRequestId(id)).unwrap()
      )
    )
      .then((responses) => {
        const prTags = responses.flatMap((r) =>
          Array.isArray(r?.data?.tags) ? r.data.tags : []
        );
        const merged = Array.from(new Set([...prTags, ...baseTags]));
        formik.setFieldValue("tags", merged);
      })
      .catch(() => {});
  }, [formik.values.prs, formik.values.with_pr, dispatch]);

  const calculateGSTValues = useCallback(
    (gstPercent: number) => {
      if (!formik.values.bill_to || !formik.values.ship_to) {
        return {
          cgst_percent: 0,
          sgst_percent: 0,
          igst_percent: 0,
        };
      }

      const supplierType = pr_supplier_data?.supplier_type
        ?.trim()
        ?.toLowerCase();

      if (supplierType === "import") {
        return {
          cgst_percent: 0,
          sgst_percent: 0,
          igst_percent: 0,
        };
      }

      if (supplierType === "local" || supplierType === "domestic") {
        const billToState = (formik.values.bill_to?.org_state || "")
          .toString()
          .trim()
          .toLowerCase();
        const shipToState = (formik.values.ship_to?.state || "")
          .toString()
          .trim()
          .toLowerCase();

        if (billToState && shipToState && billToState === shipToState) {
          const halfGST = gstPercent / 2;
          return {
            cgst_percent: halfGST,
            sgst_percent: halfGST,
            igst_percent: 0,
          };
        } else {
          return {
            cgst_percent: 0,
            sgst_percent: 0,
            igst_percent: gstPercent,
          };
        }
      }

      return {
        cgst_percent: 0,
        sgst_percent: 0,
        igst_percent: 0,
      };
    },
    [formik.values.bill_to, formik.values.ship_to, pr_supplier_data],
  );

  useEffect(() => {
    if (po_id && !initialEditLoadDoneRef.current) return;
    const currentPrs = formik?.values?.prs ?? [];
    const hasPrs = currentPrs.length > 0;
    if (!formik?.values?.supplier_id || !formik?.values?.with_pr) {
      prevPrsRef.current = currentPrs;
      return;
    }

    const prevPrs = prevPrsRef.current ?? [];
    const addedPrIds = currentPrs.filter((id: string) => !prevPrs.includes(id));
    const removedPrIds = prevPrs.filter(
      (id: string) => !currentPrs.includes(id),
    );
    const isAddingNewPr = addedPrIds.length > 0;
    const isRemovingPr = removedPrIds.length > 0;

    if (po_id && !hasSkippedInitialPRFetchRef.current) {
      hasSkippedInitialPRFetchRef.current = true;
      prevPrsRef.current = currentPrs;
      return;
    }

    prevPrsRef.current = currentPrs;

    // 1. Handle PR removal
    if (isRemovingPr) {
      const remainingProducts = formik.values.products.filter(
        (prod: any) => !removedPrIds.includes(prod.pr_id),
      );

      // Store removed products locally if they are "PO Created" (part of initial list)
      const removedProdsFromList = formik.values.products.filter((prod: any) =>
        removedPrIds.includes(prod.pr_id),
      );
      if (removedProdsFromList.length > 0) {
        setRemovedProducts((prev) => {
          const filteredNew = removedProdsFromList.filter(
            (p) =>
              !prev.some(
                (ep) =>
                  ep.product_id === p.product_id &&
                  (p.pr_id ? ep.pr_id === p.pr_id : true),
              ),
          );
          return [...prev, ...filteredNew];
        });
      }

      formik.setFieldValue("products", remainingProducts);
    }

    // 2. Handle PR addition
    if (isAddingNewPr) {
      // We ALWAYS fetch all added PRs from the API to ensures we get the most up-to-date and complete list of products.
      // This fixes the bug where manually deleted products weren't restored when re-adding a PR (because they were skipped if any item was restorable).
      const remainingAddedPrIds = addedPrIds;

      let productsAfterRestore = [...(formik.values.products ?? [])];

      const restorableProducts = removedProducts.filter((p: any) =>
        addedPrIds.includes(p.pr_id),
      );

      if (restorableProducts.length > 0) {
        productsAfterRestore = [...productsAfterRestore, ...restorableProducts];
        formik.setFieldValue("products", productsAfterRestore);
        setRemovedProducts((prev) =>
          prev.filter((p) => !addedPrIds.includes(p.pr_id)),
        );
      }

      if (remainingAddedPrIds.length > 0) {
        setPageLoading(true);
        const fetchAndMerge = async () => {
          try {
            const pr_products_res = await dispatch(
              fetchSupplierProductsByPRIDS({
                pr_ids: remainingAddedPrIds,
                supplier_id: formik.values.supplier_id,
              }),
            ).unwrap();

            if (
              !pr_products_res?.data ||
              !Array.isArray(pr_products_res.data)
            ) {
              toast.error("Invalid response received from server");
              return;
            }

            let newProducts: any[] = [];
            pr_products_res.data.forEach((pr: any) => {
              if (
                pr?.supplier_products &&
                Array.isArray(pr.supplier_products)
              ) {
                newProducts.push(...pr.supplier_products);
              }
            });

            let mappedNew = newProducts.map((prod: any) => {
              const gstValues = calculateGSTValues(prod?.gst_percent || 0);
              return {
                ...prod,
                order_quantity: prod?.update_quantity || 0,
                rate_in_currency: 0,
                ...gstValues,
                remark: "",
              };
            });

            const exchangeRateValue = formik.values.exchange_rate;
            if (exchangeRateValue && exchangeRateValue !== "") {
              const exchangeRate = Number(exchangeRateValue);
              if (exchangeRate > 0) {
                mappedNew = recalculateProductsWithExchangeRate(
                  mappedNew,
                  exchangeRate,
                );
              }
            }

            // Merge everything uniquely. API results (mappedNew) takes precedence to ensure we get the full list of products.
            const combined = [...productsAfterRestore, ...mappedNew];
            const seen = new Set();
            const merged = combined.filter((p) => {
              const key = `${p.product_id}-${p.pr_id || "direct"}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });

            formik.setFieldValue("products", merged);
          } catch (err: any) {
            console.error("Failed to fetch new PR products:", err);
            toast.error(err?.message || "Failed to fetch new PR products");
          } finally {
            setPageLoading(false);
          }
        };
        fetchAndMerge();
      }
    }
  }, [
    formik.values.prs,
    formik.values.supplier_id,
    formik.values.with_pr,
    po_id,
    dispatch,
    calculateGSTValues,
    recalculateProductsWithExchangeRate,
    removedProducts,
  ]);

  useEffect(() => {
    productsRef.current = formik.values.products;
  }, [formik.values.products]);

  // Recalculate GST values when bill_to or ship_to changes and products are already loaded
  useEffect(() => {
    const supplierType = pr_supplier_data?.supplier_type?.toLowerCase();
    if (supplierType === "import") return;
    const currentProducts = productsRef.current;
    if (
      currentProducts.length > 0 &&
      (formik.values.bill_to || formik.values.ship_to)
    ) {
      const updatedProducts = currentProducts.map((prod: any) => {
        const gstValues = calculateGSTValues(prod?.gst_percent || 0);

        const roundToTwo = (num: number) => Math.round(num * 100) / 100;
        const netTaxableValue = Number(prod?.net_taxable_value) || 0;

        const effectiveGstPercent =
          gstValues.cgst_percent +
          gstValues.sgst_percent +
          gstValues.igst_percent;
        const gstAmount = roundToTwo(
          (netTaxableValue * effectiveGstPercent) / 100,
        );
        const total = roundToTwo(netTaxableValue + gstAmount);

        return {
          ...prod,
          ...gstValues,
          gst_amount: gstAmount,
          total: total,
        };
      });
      formik.setFieldValue("products", updatedProducts);
    }
  }, [
    formik.values.bill_to,
    formik.values.ship_to,
    calculateGSTValues,
    pr_supplier_data,
  ]);

  // Recalculate products when exchange rate changes
  useEffect(() => {
    const currentProducts = productsRef.current;
    if (currentProducts.length > 0) {
      const exchangeRateValue = formik.values.exchange_rate;
      if (exchangeRateValue && exchangeRateValue !== "") {
        const exchangeRate = Number(exchangeRateValue);
        if (exchangeRate > 0) {
          const updatedProducts = recalculateProductsWithExchangeRate(
            currentProducts,
            exchangeRate,
          );
          formik.setFieldValue("products", updatedProducts);
        } else {
          const resetProducts = recalculateProductsWithExchangeRate(
            currentProducts,
            0,
          );
          formik.setFieldValue("products", resetProducts);
        }
      } else {
        const resetProducts = recalculateProductsWithExchangeRate(
          currentProducts,
          0,
        );
        formik.setFieldValue("products", resetProducts);
      }
    }
  }, [formik.values.exchange_rate]);

  // Handle currency based on supplier type
  useEffect(() => {
    if (
      !pr_supplier_data?.supplier_type ||
      !currencyOptions ||
      currencyOptions.length === 0
    )
      return;

    const supplierType = pr_supplier_data.supplier_type.toLowerCase();

    if (supplierType === "local") {
      // For local supplier: Set INR and disable currency dropdown
      const inrCurrency = currencyOptions.find(
        (c: any) => c.currency_code === "INR" || c.currency_symbol === "₹",
      );
      if (inrCurrency) {
        formik.setFieldValue("currency", inrCurrency);
        formik.setFieldValue("exchange_rate", "1");
      }
    } else if (supplierType === "import") {
      // For import supplier: Clear currency selection if it's ₹, and set exchange_rate to empty
      if (
        formik.values.currency?.currency_symbol === "₹" ||
        formik.values.currency?.currency_code === "INR"
      ) {
        formik.setFieldValue("currency", null);
      }
      // Set exchange_rate to empty for import suppliers (if it's "1" or empty)
      if (
        formik.values.exchange_rate === "1" ||
        formik.values.exchange_rate === ""
      ) {
        formik.setFieldValue("exchange_rate", "");
      }
    }
  }, [currencyOptions]);

  const totals = useMemo(() => {
    return calculateTotals(formik.values.products);
  }, [formik.values.products]);

  // Format currency with ₹ symbol and comma separators
  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <FormikProvider value={formik}>
      <Fragment>
        <PageContainer>
          <PageBreadcrumb
            title={isEdit ? "Edit Purchase Order" : "Create Purchase Order"}
            titleLink="/purchase-management/purchase-order"
            breadcrumbs={[
              {
                label: "Purchase Order",
                to: "/purchase-management/purchase-order",
              },
              {
                label: isEdit ? "Edit Purchase Order" : "Create Purchase Order",
                to: "",
              },
            ]}
            backNavigationPath={-1}
          />
          <hr />

          {pageLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "60vh" }}
            >
              <LoadingSpinner />
            </div>
          ) : (
            <Form autoComplete="off" onSubmit={formik.handleSubmit}>
              <Row>
                <Col lg={6}>
                  <div className="mb-4 mt-0">
                    <h6
                      className="fw-semibold mb-2"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Supplier Details
                    </h6>
                    <SupplierDetails
                      formik={formik}
                      pr_data={null}
                      pr_supplier_data={pr_supplier_data}
                      branchOptions={branchOptions}
                      supplierOptions={supplierOptions}
                      set_pr_supplier_data={set_pr_supplier_data}
                      supplierLoading={supplierLoading}
                      editMode
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Purchase Order Details
                    </h6>
                  </div>
                  <Row
                    style={{
                      backgroundColor: MecordColors?.LightGrayColor,
                      borderRadius: "5px",
                      padding: "10px",
                    }}
                  >
                    <Col xs={12} md={12} className="mb-3">
                      <div className="fw-semibold d-flex justify-content-between align-items-center">
                        <div>
                          <Form.Label
                            className="fw-semibold"
                            style={{
                              color: formik.values.with_pr
                                ? "inherit"
                                : "#6c757d",
                            }}
                          >
                            <span>
                              PR No.{" "}
                              {formik.values.with_pr && (
                                <span className="text-danger">*</span>
                              )}
                            </span>
                          </Form.Label>
                        </div>
                        <div>
                          {canCreateDirectPO && (
                            <div className="d-flex align-items-center">
                              <span
                                className="mb-0 me-2 fw-semibold"
                                style={{ fontSize: "0.85rem", opacity: 0.6 }}
                              >
                                With PR
                              </span>
                              <i
                                className={`bi ${
                                  formik.values.with_pr
                                    ? "bi-toggle-on text-success"
                                    : "bi-toggle-off text-danger"
                                }`}
                                style={{
                                  fontSize: "1.5rem",
                                  cursor: "default",
                                  opacity: 0.6,
                                  pointerEvents: "none",
                                }}
                                aria-disabled
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          opacity: formik.values.with_pr ? 1 : 0.6,
                          pointerEvents: formik.values.with_pr
                            ? "auto"
                            : "none",
                          filter: formik.values.with_pr
                            ? "none"
                            : "grayscale(70%)",
                        }}
                      >
                        <Autocomplete
                          multiple
                          disabled={!formik.values.with_pr}
                          disableCloseOnSelect
                          options={purchaseRequestOptions}
                          getOptionLabel={(option: any) =>
                            typeof option === "string" ? option : option.pr_no
                          }
                          value={purchaseRequestOptions.filter((pr: any) =>
                            formik.values.prs.includes(pr.pr_id),
                          )}
                          onChange={(event, selected) =>
                            formik.setFieldValue(
                              "prs",
                              selected.map((pr: any) => pr.pr_id),
                            )
                          }
                          onBlur={() => formik.setFieldTouched("prs", true)}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ padding: 0 }}>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.8,
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer",

                                  // ✅ Hover state
                                  "&:hover": {
                                    backgroundColor: MecordColors.Hover,
                                    color: MecordColors.ActiveText,
                                  },

                                  // ✅ Selected state
                                  ...(selected && {
                                    backgroundColor:
                                      MecordColors.ActiveBackground,
                                    color: MecordColors.ActiveText,
                                  }),
                                }}
                              >
                                {/* Option Label */}
                                <span>
                                  {typeof option === "string"
                                    ? option
                                    : option.pr_no}
                                </span>

                                {/* Show X only if option is selected */}
                                {selected && (
                                  <span
                                    style={{
                                      cursor: "pointer",
                                      marginLeft: "10px",
                                      color: "#d32f2f",
                                      fontWeight: "bold",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation(); // prevent normal selection toggling
                                      const newValues =
                                        formik.values.prs.filter(
                                          // (pr: any) => pr?.pr_id !== option.pr_id,
                                          (id: any) => id !== option.pr_id,
                                        );
                                      formik.setFieldValue("prs", newValues);
                                    }}
                                  >
                                    ✕
                                  </span>
                                )}
                              </Box>
                            </li>
                          )}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderInput={(params) => (
                            <BootstrapTextField
                              {...params}
                              placeholder="Select Purchase Request"
                              name="prs"
                              error={
                                formik.touched.prs && Boolean(formik.errors.prs)
                              }
                              helperText={
                                formik.touched.prs &&
                                typeof formik.errors.prs === "string"
                                  ? formik.errors.prs
                                  : ""
                              }
                              fullWidth
                              size="small"
                              sx={{
                                // background: "#FFF",
                                ...autocompleteDropdownStyles.inputSx,
                              }}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option: any, index: number) => {
                              const tagProps = getTagProps({ index });

                              return (
                                <Box
                                  key={option.pr_id}
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    backgroundColor:
                                      MecordColors.ActiveBackground,
                                    borderRadius: "3px",
                                    padding: "2px 6px",
                                    margin: "1px",
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "13px",
                                  }}
                                >
                                  {option.pr_no}

                                  <span
                                    style={{
                                      cursor: "pointer",
                                      marginLeft: "10px",
                                      color: "#d32f2f",
                                      fontWeight: "bold",
                                    }}
                                    onClick={tagProps.onDelete} // ✅ use MUI delete handler
                                  >
                                    ✕
                                  </span>
                                </Box>
                              );
                            })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#ced4da",
                              },
                              "&:hover fieldset": {
                                borderColor: "#ced4da",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#ced4da",
                              },
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "13px",
                            },
                          }}
                        />
                      </div>
                    </Col>
                    <Col lg={6} className="mb-3">
                      <Form.Label className="fw-semibold">
                        PO Date<span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="po_date"
                        value={formik.values.po_date}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.po_date && formik.touched.po_date
                        }
                        style={{ backgroundColor: "#ffffff", color: "#000" }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.po_date}
                      </Form.Control.Feedback>
                    </Col>

                    <Col lg={6} className="mb-3">
                      <Form.Label className="fw-semibold">
                        Initiated By <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="initiated_by"
                        placeholder="Enter Name"
                        value={formik.values.initiated_by}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.initiated_by &&
                          formik.touched.initiated_by
                        }
                        style={{ backgroundColor: "#ffffff", color: "#000" }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.initiated_by}
                      </Form.Control.Feedback>
                    </Col>

                    <Col lg={6} className="mb-3">
                      <Form.Label className="fw-semibold">ETA Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="eta_date"
                        value={formik.values.eta_date}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.eta_date && formik.touched.eta_date
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.eta_date}
                      </Form.Control.Feedback>
                    </Col>
                    <Col xs={12} className="mb-3">
                      <AddTagsForPurchaseOrder
                        label="Tags"
                        placeholder="Enter tags"
                        value={formik.values.tags}
                        onChange={(newTags) => {
                          const prev = formik.values.tags ?? [];
                          const added = newTags.filter((t) => !prev.includes(t));
                          const removed = prev.filter((t) => !newTags.includes(t));
                          added.forEach((t) => customTagsRef.current.add(t));
                          removed.forEach((t) => customTagsRef.current.delete(t));
                          formik.setFieldValue("tags", newTags);
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              <div className="my-4">
                <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                  Billing & Shipping Details
                </h6>
                <Row>
                  <Col lg={6} className="mb-0 mt-2">
                    <Form.Label className="fw-semibold">
                      Ship To<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      options={shipToOptions}
                      getOptionLabel={(option: any) =>
                        `${option?.warehouse_name} (${option?.address},${option?.city},${option?.state},${option?.country})` ||
                        ""
                      }
                      value={
                        shipToOptions.find(
                          (opt: any) =>
                            opt.warehouse_id ===
                            formik.values.ship_to?.warehouse_id,
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        formik.setFieldValue("ship_to", newValue || "");
                      }}
                      onBlur={() => formik.setFieldTouched("ship_to", true)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ padding: 0 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.8,
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",

                              // ✅ Hover state
                              "&:hover": {
                                backgroundColor: MecordColors.Hover,
                                color: MecordColors.ActiveText,
                              },

                              // ✅ Selected state
                              ...(selected && {
                                backgroundColor: MecordColors.ActiveBackground,
                                color: MecordColors.ActiveText,
                              }),
                            }}
                          >
                            {/* Option Label */}
                            <span>
                              {`${option?.warehouse_name} (${option?.address},${option?.city},${option?.state},${option?.country})` ||
                                ""}
                            </span>
                          </Box>
                        </li>
                      )}
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          id="ship_to"
                          name="ship_to"
                          placeholder="Ship To"
                          error={Boolean(
                            formik.touched.ship_to && formik.errors.ship_to,
                          )}
                          helperText={
                            formik.touched.ship_to &&
                            typeof formik.errors.ship_to === "string"
                              ? formik.errors.ship_to
                              : ""
                          }
                          fullWidth
                          size="small"
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  </Col>

                  <Col lg={6} className="mb-0 mt-2">
                    <Form.Label className="fw-semibold">
                      Bill To<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      options={billToOptions}
                      getOptionLabel={(option: any) =>
                        `${option?.organization_name} (${option?.org_address},${option?.org_city},${option?.org_state},${option?.org_country})` ||
                        ""
                      }
                      value={
                        billToOptions.find(
                          (opt: any) =>
                            opt.organization_id ===
                            formik.values.bill_to?.organization_id,
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        formik.setFieldValue("bill_to", newValue || "");
                      }}
                      onBlur={() => formik.setFieldTouched("bill_to", true)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ padding: 0 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.8,
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",

                              // ✅ Hover state
                              "&:hover": {
                                backgroundColor: MecordColors.Hover,
                                color: MecordColors.ActiveText,
                              },

                              // ✅ Selected state
                              ...(selected && {
                                backgroundColor: MecordColors.ActiveBackground,
                                color: MecordColors.ActiveText,
                              }),
                            }}
                          >
                            {/* Option Label */}
                            <span>
                              {`${option?.organization_name} (${option?.org_address},${option?.org_city},${option?.org_state},${option?.org_country})` ||
                                ""}
                            </span>
                          </Box>
                        </li>
                      )}
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          id="bill_to"
                          name="bill_to"
                          placeholder="Bill To"
                          error={Boolean(
                            formik.touched.ship_to && formik.errors.bill_to,
                          )}
                          helperText={
                            formik.touched.bill_to &&
                            typeof formik.errors.bill_to === "string"
                              ? formik.errors.bill_to
                              : ""
                          }
                          fullWidth
                          size="small"
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  </Col>
                </Row>
              </div>

              <div className="my-4">
                <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                  Currency & Exchange Details
                </h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">
                        Currency<span className="text-danger">*</span>
                      </Form.Label>
                      <Autocomplete
                        disableClearable
                        disabled={
                          pr_supplier_data?.supplier_type?.toLowerCase() ===
                          "local"
                        }
                        options={currencyOptions}
                        getOptionLabel={(option: any) =>
                          option?.currency_name
                            ? `${option.currency_code} - ${option.currency_name}`
                            : ""
                        }
                        getOptionDisabled={(option: any) => {
                          // Disable ₹ option for import supplier type
                          if (
                            pr_supplier_data?.supplier_type?.toLowerCase() ===
                            "import"
                          ) {
                            return (
                              option?.currency_symbol === "₹" ||
                              option?.currency_code === "INR"
                            );
                          }
                          return false;
                        }}
                        isOptionEqualToValue={(option: any, value: any) => {
                          const optionCode =
                            typeof option === "string"
                              ? option
                              : option?.currency_code;
                          const valueCode =
                            typeof value === "string"
                              ? value
                              : value?.currency_code;
                          return optionCode === valueCode;
                        }}
                        value={
                          formik.values.currency
                            ? (currencyOptions || []).find(
                                (c: any) =>
                                  c.currency_code ===
                                  formik.values.currency?.currency_code,
                              ) || null
                            : null
                        }
                        onChange={(_: any, newValue: any) => {
                          const currencyCode = newValue?.currency_code || "";
                          const currencySymbol =
                            newValue?.currency_symbol || "";
                          formik.setFieldValue("currency", newValue);
                          if (currencySymbol === "₹") {
                            formik.setFieldValue("exchange_rate", "1");
                          } else {
                            formik.setFieldValue("exchange_rate", "");
                          }
                        }}
                        onBlur={() => {
                          formik.setFieldTouched("currency", true);
                        }}
                        loading={currencyLoading}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} style={{ padding: 0 }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.8,
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",

                                // ✅ Hover state
                                "&:hover": {
                                  backgroundColor: MecordColors.Hover,
                                  color: MecordColors.ActiveText,
                                },

                                // ✅ Selected state
                                ...(selected && {
                                  backgroundColor:
                                    MecordColors.ActiveBackground,
                                  color: MecordColors.ActiveText,
                                }),
                              }}
                            >
                              {/* Option Label */}
                              <span>
                                {option?.currency_name
                                  ? `${option.currency_code} - ${option.currency_name}`
                                  : ""}
                              </span>
                            </Box>
                          </li>
                        )}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderInput={(params: any) => (
                          <BootstrapTextField
                            {...params}
                            id="currency"
                            name="currency"
                            placeholder="Select Currency"
                            size="small"
                            error={
                              !!formik.errors.currency &&
                              formik.touched.currency
                            }
                            helperText={
                              formik.errors.currency && formik.touched.currency
                                ? formik.errors.currency
                                : ""
                            }
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                            }}
                          />
                        )}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#ced4da",
                            },
                            "&:hover fieldset": {
                              borderColor: "#ced4da",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#ced4da",
                            },
                          },
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">
                        Exchange Rate (in INR){" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id="exchange_rate"
                        name="exchange_rate"
                        placeholder="Enter Exchange Rate"
                        value={formik.values.exchange_rate || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            formik.setFieldValue("exchange_rate", value);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        disabled={(() => {
                          // For import suppliers, keep exchange rate enabled even if no currency selected
                          const supplierType =
                            pr_supplier_data?.supplier_type?.toLowerCase();
                          if (supplierType === "import") {
                            // Only disable if currency is ₹
                            if (formik.values.currency) {
                              const selectedCurrency = currencyOptions.find(
                                (opt: any) =>
                                  opt.currency_code ===
                                  formik.values.currency?.currency_code,
                              );
                              return selectedCurrency?.currency_symbol === "₹";
                            }
                            return false; // Enabled for import suppliers even without currency
                          }
                          // For local suppliers or other cases
                          if (
                            !formik.values.currency ||
                            !currencyOptions ||
                            currencyOptions.length === 0
                          ) {
                            return true; // Default to disabled if no currency selected
                          }
                          const selectedCurrency = currencyOptions.find(
                            (opt: any) =>
                              opt.currency_code ===
                              formik.values.currency?.currency_code,
                          );
                          return selectedCurrency?.currency_symbol === "₹";
                        })()}
                        isInvalid={
                          !!formik.errors.exchange_rate &&
                          formik.touched.exchange_rate
                        }
                      />
                      {formik.errors.exchange_rate &&
                        formik.touched.exchange_rate && (
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.exchange_rate}
                          </Form.Control.Feedback>
                        )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <ProductFormTable
                mainFormik={formik}
                isWithPR={formik.values.with_pr}
                calculateGSTValues={calculateGSTValues}
                pr_supplier_data={pr_supplier_data}
                onRemoveProduct={(prod: any) =>
                  setRemovedProducts((prev) => {
                    if (
                      prev.some(
                        (p) =>
                          p.product_id === prod.product_id &&
                          (prod.pr_id ? p.pr_id === prod.pr_id : true),
                      )
                    ) {
                      return prev;
                    }
                    return [...prev, prod];
                  })
                }
              />

              <style>
                {`
                .accordion-notes .accordion-button,
                .accordion-attach-files .accordion-button {
                  background-color: ${MecordColors?.LightGrayColor} !important;
                  color: ${MecordColors?.NormalText} !important;
                }
                .accordion-notes .accordion-button:not(.collapsed),
                .accordion-attach-files .accordion-button:not(.collapsed) {
                  background-color: ${MecordColors?.LightGrayColor} !important;
                  color: ${MecordColors?.NormalText} !important;
                }
              `}
              </style>
              <Row className="mb-4 mt-3">
                <Col md={6}>
                  <h6
                    className="fw-semibold mb-3"
                    style={{ fontSize: "0.9rem" }}
                  >
                    Notes & Attachments
                  </h6>
                  {/* <Accordion
                  defaultActiveKey="0"
                  className="mb-3 accordion-notes"
                >
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <span className="fw-semibold">Notes</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Form.Group className="mb-3">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="notes"
                          placeholder="Enter your business Terms & Conditions"
                          value={formik.values.notes || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Form.Group>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion> */}

                  <div className="rounded mb-3">
                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Terms & Condition
                    </h6>
                    <div className="p-3">
                      <Row className="g-3 mb-3">
                        <Col xs={12} sm={12} md={4}>
                          <Form.Group>
                            <Form.Label>Type</Form.Label>
                            <Autocomplete
                              options={termTypeOptions}
                              getOptionLabel={(option: any) => option}
                              isOptionEqualToValue={(option: any, value: any) =>
                                option === value
                              }
                              value={newTerm.type || null}
                              loading={termsLoading}
                              disabled={termsLoading}
                              onChange={(_: any, newValue: any) => {
                                const currentTerms = Array.isArray(
                                  formik.values.terms_and_conditions,
                                )
                                  ? formik.values.terms_and_conditions
                                  : [];
                                const existingTerm = currentTerms.find(
                                  (t: any) => t.type === newValue,
                                );
                                const existingOptions = existingTerm
                                  ? getTermOptions(existingTerm)
                                  : [];

                                setNewTerm({
                                  type: newValue || "",
                                  options: existingOptions.length
                                    ? [existingOptions[0]]
                                    : [],
                                });
                              }}
                              ListboxProps={
                                autocompleteDropdownStyles.ListboxProps
                              }
                              renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                  <li key={key} {...rest}>
                                    {option}
                                  </li>
                                );
                              }}
                              renderInput={(params) => (
                                <BootstrapTextField
                                  {...params}
                                  placeholder={
                                    termsLoading ? "Loading..." : "Select Type"
                                  }
                                  size="small"
                                  sx={autocompleteDropdownStyles.inputSx}
                                />
                              )}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} sm={12} md={8}>
                          <Form.Group>
                            <Form.Label>Options</Form.Label>
                            <Autocomplete
                              options={getAvailableOptions()}
                              getOptionLabel={(option: any) => option}
                              isOptionEqualToValue={(option: any, value: any) =>
                                option === value
                              }
                              value={newTerm.options?.[0] ?? null}
                              disabled={!newTerm.type}
                              onChange={(_: any, newValue: any) => {
                                setNewTerm({
                                  ...newTerm,
                                  options: newValue ? [newValue] : [],
                                });
                              }}
                              ListboxProps={
                                autocompleteDropdownStyles.ListboxProps
                              }
                              renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                  <li key={key} {...rest}>
                                    <span style={{ fontSize: "0.875rem" }}>
                                      {option}
                                    </span>
                                  </li>
                                );
                              }}
                              renderInput={(params) => (
                                <BootstrapTextField
                                  {...params}
                                  placeholder="Select Options"
                                  size="small"
                                  sx={autocompleteDropdownStyles.inputSx}
                                />
                              )}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end align-items-end mb-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAddTerm}
                          disabled={
                            !newTerm.type || newTerm.options.length === 0
                          }
                          style={{
                            backgroundColor: MecordColors.ThemeBGColor,
                            borderColor: MecordColors.ThemeBGColor,
                            minWidth: "80px",
                          }}
                        >
                          <Plus size={16} className="me-1" /> Add
                        </Button>
                      </div>

                      <FieldArray name="terms_and_conditions">
                        {({ remove }) => (
                          <div>
                            {formik.values.terms_and_conditions &&
                            formik.values.terms_and_conditions.length > 0 ? (
                              <div className="d-flex flex-column gap-3">
                                {formik.values.terms_and_conditions.map(
                                  (term: any, index: number) => {
                                    const options = getTermOptions(term);
                                    return (
                                      <div
                                        key={term.id || index}
                                        className="p-3 border rounded position-relative"
                                        style={{ backgroundColor: "#fff" }}
                                      >
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="w-100">
                                            <h6 className="fw-bold mb-2">
                                              {term.type}
                                            </h6>
                                            {options.length > 0 ? (
                                              <ul
                                                className="mb-0 ps-3"
                                                style={{ fontSize: "14px" }}
                                              >
                                                {options.map(
                                                  (opt, optIndex) => (
                                                    <li
                                                      key={optIndex}
                                                      style={{
                                                        marginBottom: "4px",
                                                      }}
                                                    >
                                                      {opt}
                                                    </li>
                                                  ),
                                                )}
                                              </ul>
                                            ) : (
                                              <div style={{ fontSize: "14px" }}>
                                                {term.description ||
                                                  term.options}
                                              </div>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            className="btn btn-sm p-1 ms-2"
                                            style={{
                                              backgroundColor: "#ffe5e5",
                                              color: "#dc3545",
                                              border: "none",
                                              borderRadius: "4px",
                                              minWidth: "32px",
                                              height: "32px",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <Trash size={14} />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-muted py-4">
                                No terms and conditions added yet.
                              </div>
                            )}
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  </div>

                  <div
                    className="rounded mb-3"
                    style={{ backgroundColor: MecordColors?.LightGrayColor }}
                  >
                    {/* Notes Header - Collapsible */}
                    <div
                      className="d-flex justify-content-start align-items-center p-2 rounded"
                      style={{
                        cursor:
                          !formik.values.notes ||
                          formik.values.notes.trim() === ""
                            ? "pointer"
                            : "default",
                        backgroundColor: MecordColors?.LightGrayColor,
                      }}
                      onClick={() => {
                        // Only allow closing if notes are empty
                        if (
                          !formik.values.notes ||
                          formik.values.notes.trim() === ""
                        ) {
                          setIsNotesOpen(!isNotesOpen);
                        }
                      }}
                    >
                      {(!formik.values.notes ||
                        formik.values.notes.trim() === "") && (
                        <div className="me-1">
                          {isNotesOpen ? (
                            <ChevronUp size={20} />
                          ) : (
                            <ChevronDown size={20} />
                          )}
                        </div>
                      )}

                      <h6
                        className="fw-semibold mb-0"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Notes
                      </h6>
                    </div>

                    {/* Notes Content - Collapsible */}
                    {isNotesOpen && (
                      <div className="p-0">
                        <Form.Group>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="notes"
                            placeholder="Enter your notes"
                            value={formik.values.notes || ""}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            style={{ maxHeight: "400px", resize: "vertical" }}
                            isInvalid={
                              formik.touched.notes && !!formik.errors.notes
                            }
                          />

                          <Form.Control.Feedback type="invalid">
                            {formik.touched.notes && formik.errors.notes}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    )}
                  </div>

                  <Accordion
                    defaultActiveKey="0"
                    className="mb-3 accordion-attach-files"
                  >
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <span className="fw-semibold">Attach Files</span>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Form.Group className="mb-3">
                          <FileUpload
                            label=""
                            multiple={true}
                            value={formik.values.attachments} // 👈 NEW FILES ONLY
                            existingFiles={existingDocuments} // 👈 EXISTING DOCS
                            onChange={(files) =>
                              formik.setFieldValue("attachments", files)
                            }
                            onRemoveExisting={(index) => {
                              const updatedDocs = [...existingDocuments];
                              updatedDocs.splice(index, 1);
                              setExistingDocuments(updatedDocs);

                              // Optional: track deleted docs for backend
                              // setDeletedDocuments([...deletedDocuments, existingDocuments[index]]);
                            }}
                          />
                        </Form.Group>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Col>
                <Col md={6}>
                  <h6
                    className="fw-semibold mb-3"
                    style={{ fontSize: "0.9rem" }}
                  >
                    Total Summary
                  </h6>
                  <div
                    style={{
                      backgroundColor: MecordColors?.LightThemeBGColor,
                      padding: "20px",
                      borderRadius: "8px",
                      border: `1px solid ${MecordColors?.LightThemeBGColor}`,
                    }}
                  >
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal</span>
                      <span>
                        {formik?.values?.currency?.currency_symbol}{" "}
                        {formatIndianAmount(totals.subtotal)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total GST Value</span>
                      <span>
                        {formik?.values?.currency?.currency_symbol}{" "}
                        {formatIndianAmount(totals.totalGst)}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between"
                      style={{
                        borderTop: `1px solid ${MecordColors?.LightThemeBGColor}`,
                        paddingTop: "10px",
                        marginTop: "10px",
                      }}
                    >
                      <span className="fw-bold">Grand Amount</span>
                      <span className="fw-bold">
                        {formik?.values?.currency?.currency_symbol}{" "}
                        {formatIndianAmount(totals.grandTotal)}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
                <CancelButton
                  onClick={() =>
                    navigate("/purchase-management/purchase-order")
                  }
                  redirectTo="/purchase-management/purchase-order"
                  disabled={loading || pageLoading}
                />
                <PrimarySubmitButton
                  label="Submit"
                  isLoading={loading}
                  loadingLabel="Submitting..."
                  type="submit"
                />
              </div>
            </Form>
          )}
        </PageContainer>
      </Fragment>
    </FormikProvider>
  );
};

export default EditPurchaseOrder;
